import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingFAQ from "../components/FloatingFAQ";
import PageMeta from "../components/PageMeta";

const websitePlans = [
  {
    name: "Starter",
    price: "999",
    description: "Perfect for small businesses just getting started online.",
    features: [
      "5-page responsive website",
      "Mobile-friendly design",
      "Basic SEO setup",
      "Contact form integration",
      "1 month of support included"
    ],
    featured: false
  },
  {
    name: "Professional",
    price: "2,499",
    description: "For growing businesses that need more features and support.",
    features: [
      "10-page custom website",
      "Advanced responsive design",
      "Full SEO optimization",
      "CMS integration",
      "Analytics & reporting",
      "3 months of support included"
    ],
    featured: true
  },
  {
    name: "Enterprise",
    price: "4,999",
    description: "Full-scale digital transformation for established businesses.",
    features: [
      "Unlimited pages",
      "Custom software development",
      "Comprehensive SEO strategy",
      "Marketing campaigns",
      "Dedicated account manager",
      "12 months of support included"
    ],
    featured: false
  }
];

const softwarePlans = [
  {
    name: "Workflow Automation",
    price: "From $999",
    description: "Automate repetitive tasks and business processes so your team saves hours every week."
  },
  {
    name: "AI Chatbots",
    price: "From $1,499",
    description: "Intelligent bots for customer service and sales that work around the clock."
  },
  {
    name: "AI Integration",
    price: "From $2,499",
    description: "Connect AI capabilities to the systems and tools you already use."
  },
  {
    name: "Custom Software",
    price: "From $4,999",
    description: "CRM systems, dashboards, inventory tools, and APIs built for your exact workflow."
  }
];

const marketingPlans = [
  {
    name: "SEO Services",
    price: "$999",
    description: "Comprehensive search engine optimization with monthly reporting."
  },
  {
    name: "PPC Management",
    price: "$1,499",
    description: "Expertly managed paid advertising campaigns on Google and Meta."
  },
  {
    name: "Content Marketing",
    price: "$1,999",
    description: "Strategic content that attracts your audience and converts."
  },
  {
    name: "Full Service Marketing",
    price: "$3,999",
    description: "Complete digital marketing management with a dedicated strategist."
  }
];

const carePlans = [
  {
    name: "Hosting",
    price: "20",
    description: "We keep your website online, fast, and secure so you never have to think about it.",
    features: [
      "Managed cloud hosting",
      "SSL certificate included",
      "Daily backups",
      "Uptime monitoring"
    ]
  },
  {
    name: "Care & Support",
    price: "50",
    description: "Something broken or not working right? We troubleshoot and fix it as part of your plan.",
    features: [
      "Troubleshooting & bug fixes",
      "Security updates",
      "Priority email support",
      "Response within 24 hours"
    ]
  }
];

const SectionHeading = ({ tag, children, sub }) => (
  <div className="text-center mb-12">
    {tag && <span className="tag mb-4">{tag}</span>}
    <h2 className="text-3xl md:text-5xl font-bold mt-4 mb-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>
      {children}
    </h2>
    {sub && <p className="text-base max-w-xl mx-auto" style={{ color: '#A1A1AA' }}>{sub}</p>}
  </div>
);

export default function Pricing() {
  return (
    <div className="noise-bg">
      <PageMeta
        title="Pricing | Averoan Technologies"
        description="Transparent pricing for websites, custom software, AI automation, and growth marketing. Your first website from $500. Hosting $20 per month. Vancouver, BC."
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 relative" data-testid="pricing-hero">
        <div className="hero-glow absolute top-0 left-1/2 -translate-x-1/2 opacity-30" />
        <div className="container-main relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="tag mb-6">Pricing</span>
            <h1
              className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6"
              style={{ fontFamily: '"Clash Display", sans-serif' }}
            >
              Simple<br />
              <span style={{ color: '#D4FF00' }}>pricing</span>
            </h1>
            <p
              className="text-base md:text-lg max-w-lg mx-auto"
              style={{ color: '#A1A1AA' }}
            >
              Transparent pricing across everything we do. Websites, software, AI, and marketing.
            </p>
          </div>
        </div>
      </section>

      {/* Intro Offer */}
      <section className="pb-16" data-testid="pricing-intro-offer">
        <div className="container-main">
          <div
            className="pricing-card featured relative max-w-5xl mx-auto"
            data-testid="intro-offer-card"
          >
            <span className="pricing-badge">Limited Intro Offer</span>
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
              <div>
                <h2
                  className="text-3xl md:text-4xl font-bold mb-3 normal-case"
                  style={{ fontFamily: '"Clash Display", sans-serif' }}
                >
                  Your first website for <span style={{ color: '#D4FF00' }}>$500</span>
                </h2>
                <p className="text-sm md:text-base max-w-xl" style={{ color: '#A1A1AA' }}>
                  New to Averoan? Get a professional 4 page website with custom design,
                  contact form, and basic SEO for just $500.
                </p>
              </div>
              <Link to="/consultation" className="btn-primary whitespace-nowrap" data-testid="intro-offer-cta">
                Claim Offer <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Website Pricing */}
      <section className="section-padding" style={{ background: '#18181B' }} data-testid="pricing-cards">
        <div className="container-main">
          <SectionHeading tag="Web Development">
            Website <span style={{ color: '#D4FF00' }}>projects</span>
          </SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {websitePlans.map((plan) => (
              <div
                key={plan.name}
                className={`pricing-card relative ${plan.featured ? 'featured' : ''}`}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {plan.featured && (
                  <span className="pricing-badge">Most Popular</span>
                )}

                <h3
                  className="text-2xl font-bold mb-2 normal-case"
                  style={{ fontFamily: '"Clash Display", sans-serif' }}
                >
                  {plan.name}
                </h3>

                <div className="mb-2">
                  {plan.introPrice ? (
                    <>
                      <span className="text-4xl font-bold" style={{ color: '#D4FF00' }}>${plan.introPrice}</span>
                      <span className="text-xl line-through ml-3" style={{ color: '#A1A1AA' }}>${plan.price}</span>
                      <span style={{ color: '#A1A1AA' }}>/project</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold">${plan.price}</span>
                      <span style={{ color: '#A1A1AA' }}>/project</span>
                    </>
                  )}
                </div>
                {plan.introPrice && (
                  <p className="text-xs mb-2 font-medium" style={{ color: '#D4FF00' }}>
                    Intro offer for new clients
                  </p>
                )}

                <p className="text-sm mb-8" style={{ color: '#A1A1AA' }}>{plan.description}</p>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm" style={{ color: '#A1A1AA' }}>
                      <Check size={16} style={{ color: '#D4FF00', marginTop: '2px', flexShrink: 0 }} />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/consultation"
                  className={plan.featured ? 'btn-primary w-full text-center' : 'btn-outline w-full text-center'}
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Software & AI Pricing */}
      <section className="section-padding" data-testid="pricing-software-ai">
        <div className="container-main">
          <SectionHeading
            tag="Software & AI"
            sub="Custom software and AI automation, scoped to your business. Every project starts with a free consultation and a fixed quote."
          >
            Software & <span style={{ color: '#D4FF00' }}>AI automation</span>
          </SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {softwarePlans.map((plan) => (
              <div key={plan.name} className="pricing-card relative flex flex-col" data-testid={`software-card-${plan.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <h3 className="text-xl font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className="text-2xl font-bold" style={{ color: '#D4FF00' }}>{plan.price}</span>
                </div>
                <p className="text-sm mb-8 flex-1" style={{ color: '#A1A1AA' }}>{plan.description}</p>
                <Link to="/consultation" className="btn-outline w-full text-center">
                  Get a Quote
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marketing Pricing */}
      <section className="section-padding" style={{ background: '#18181B' }} data-testid="pricing-marketing">
        <div className="container-main">
          <SectionHeading
            tag="Growth Marketing"
            sub="Monthly marketing plans that put your business in front of the right audience. Cancel anytime."
          >
            Growth <span style={{ color: '#D4FF00' }}>marketing</span>
          </SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {marketingPlans.map((plan) => (
              <div key={plan.name} className="pricing-card relative flex flex-col" data-testid={`marketing-card-${plan.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <h3 className="text-xl font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className="text-2xl font-bold" style={{ color: '#D4FF00' }}>From {plan.price}</span>
                  <span style={{ color: '#A1A1AA' }}>/month</span>
                </div>
                <p className="text-sm mb-8 flex-1" style={{ color: '#A1A1AA' }}>{plan.description}</p>
                <Link to="/consultation" className="btn-outline w-full text-center">
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hosting & Care Plans */}
      <section className="section-padding" data-testid="pricing-monthly">
        <div className="container-main">
          <SectionHeading
            tag="After Launch"
            sub="Keep your site online and healthy after launch. Cancel anytime, no contracts."
          >
            Hosting & <span style={{ color: '#D4FF00' }}>care plans</span>
          </SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {carePlans.map((plan) => (
              <div
                key={plan.name}
                className="pricing-card relative"
                data-testid={`monthly-plan-${plan.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <h3 className="text-2xl font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">${plan.price}</span>
                  <span style={{ color: '#A1A1AA' }}>/month</span>
                </div>
                <p className="text-sm mb-6" style={{ color: '#A1A1AA' }}>{plan.description}</p>
                <ul className="space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm" style={{ color: '#A1A1AA' }}>
                      <Check size={16} style={{ color: '#D4FF00', marginTop: '2px', flexShrink: 0 }} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" style={{ background: '#18181B' }} data-testid="pricing-faq">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto">
            <h2
              className="text-4xl md:text-6xl font-bold mb-6"
              style={{ fontFamily: '"Clash Display", sans-serif' }}
            >
              Have<br />
              <span style={{ color: '#D4FF00' }}>questions?</span>
            </h2>
            <p className="text-base md:text-lg mb-10" style={{ color: '#A1A1AA' }}>
              Every project is unique. Contact us to discuss your specific needs and get a custom quote.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="pricing-contact-btn">
              Contact Us <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingFAQ />
    </div>
  );
}
